/**
 * CurrencyContext — multi-currency display layer.
 *
 * SociBaba prices are stored in INR (seller base). This context fetches the
 * live currency ladder from `/api/currency/config`, resolves the shopper's
 * preferred display currency (localStorage → browser locale → INR default),
 * and exposes a `formatPrice(inrAmount)` helper every shopper surface calls.
 *
 * IMPORTANT — display only. Checkout still bills in INR via Razorpay because
 * payment providers only accept their own supported currencies. The cart /
 * checkout pages surface a small "Billed in INR" disclosure so shoppers
 * understand what will hit their card.
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface CurrencyRow {
  code: string;
  symbol: string;
  name: string;
  rate: number;   // display units per 1 INR
  locale: string;
  flag?: string;
}

interface CurrencyConfigPayload {
  base: string;
  currencies: CurrencyRow[];
  note?: string;
}

interface CurrencyCtx {
  base: string;
  code: string;                // currently selected code
  currency: CurrencyRow;       // full row for the current code
  currencies: CurrencyRow[];   // full ladder
  setCode: (code: string) => void;
  formatPrice: (inrAmount: number | null | undefined, opts?: { compact?: boolean; hideDecimals?: boolean }) => string;
  convertToDisplay: (inrAmount: number) => number;
  note?: string;
  isLoading: boolean;
}

const STORAGE_KEY = "sociBaba.currency";

const DEFAULT_LADDER: CurrencyRow[] = [
  { code: "INR", symbol: "\u20B9", name: "Indian Rupee",     rate: 1.0,     locale: "en-IN", flag: "\uD83C\uDDEE\uD83C\uDDF3" },
  { code: "USD", symbol: "$",      name: "US Dollar",        rate: 0.01205, locale: "en-US", flag: "\uD83C\uDDFA\uD83C\uDDF8" },
  { code: "EUR", symbol: "\u20AC", name: "Euro",             rate: 0.01111, locale: "en-IE", flag: "\uD83C\uDDEA\uD83C\uDDFA" },
  { code: "GBP", symbol: "\u00A3", name: "British Pound",    rate: 0.00952, locale: "en-GB", flag: "\uD83C\uDDEC\uD83C\uDDE7" },
  { code: "AED", symbol: "AED",    name: "UAE Dirham",       rate: 0.04425, locale: "en-AE", flag: "\uD83C\uDDE6\uD83C\uDDEA" },
];

const CurrencyContext = createContext<CurrencyCtx | null>(null);

// Auto-detect a sensible default currency from the browser locale.
// - Indian browsers stay on INR (the base).
// - en-US / en-GB / en-IE / en-AE map to their local ladder entry.
// - Everyone else defaults to USD (widest ecommerce coverage).
function detectDefaultCode(currencies: CurrencyRow[]): string {
  if (typeof navigator === "undefined") return "INR";
  const lang = (navigator.language || "").toLowerCase();
  // Direct locale hit — pick the currency row whose locale prefix matches.
  const hit = currencies.find((c) => lang.startsWith(c.locale.toLowerCase()));
  if (hit) return hit.code;
  // Region → currency shortcuts.
  if (lang.includes("-in") || lang === "hi") return "INR";
  if (lang.includes("-us")) return "USD";
  if (lang.includes("-gb")) return "GBP";
  if (lang.includes("-ae")) return "AED";
  if (lang.includes("-de") || lang.includes("-fr") || lang.includes("-es") || lang.includes("-it") || lang.includes("-nl")) return "EUR";
  return "USD";
}

// Best-effort IP-based detection. The backend hits a free geo service and
// maps the resolved country to one of our supported currencies (or null
// when the lookup fails, in which case we fall back to browser locale).
async function detectFromIP(): Promise<string | null> {
  try {
    const { data } = await api.get<{ currency: string | null }>("/currency/detect");
    return (data?.currency || null);
  } catch {
    return null;
  }
}

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data, isLoading } = useQuery<CurrencyConfigPayload>({
    queryKey: ["currency-config"],
    queryFn: async () => (await api.get("/currency/config")).data,
    staleTime: 60 * 60_000,
    gcTime: 24 * 60 * 60_000,
  });
  const currencies = data?.currencies?.length ? data.currencies : DEFAULT_LADDER;
  const base = data?.base || "INR";

  // Whether the visitor already has an explicit preference saved. If yes,
  // never touch the code again — auto-detection would fight their choice.
  const [code, setCodeState] = useState<string>(() => {
    if (typeof window === "undefined") return "INR";
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved || "INR";
  });

  // Run auto-detection at most ONCE per session (or until a preference is
  // saved). The previous implementation had `code` and `currencies` in the
  // effect deps, which caused a re-detection loop every time the ladder
  // refetched or the geo response drifted between calls — producing the
  // visible header flicker (USD ⇄ INR ⇄ USD).
  const detectedRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (detectedRef.current) return;                                // already ran
    if (window.localStorage.getItem(STORAGE_KEY)) {                  // explicit choice
      detectedRef.current = true;
      return;
    }
    if (isLoading || !currencies.length) return;                     // wait for ladder
    detectedRef.current = true;                                      // gate BEFORE async
    let cancelled = false;
    (async () => {
      const supportedCodes = new Set(currencies.map((c) => c.code));
      const ipCode = await detectFromIP();
      if (cancelled) return;
      const resolved =
        (ipCode && supportedCodes.has(ipCode)) ? ipCode :
        (detectDefaultCode(currencies) || null);
      if (!resolved) return;
      // Persist immediately so the next mount / refetch skips detection.
      window.localStorage.setItem(STORAGE_KEY, resolved);
      setCodeState((prev) => (prev === resolved ? prev : resolved));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const setCode = useCallback((next: string) => {
    setCodeState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  const currency = useMemo(
    () => currencies.find((c) => c.code === code) || currencies[0],
    [currencies, code],
  );

  const convertToDisplay = useCallback(
    (inrAmount: number) => inrAmount * (currency?.rate ?? 1),
    [currency?.rate],
  );

  const formatPrice = useCallback(
    (inrAmount: number | null | undefined, opts?: { compact?: boolean; hideDecimals?: boolean }) => {
      if (inrAmount == null || Number.isNaN(inrAmount)) return "—";
      const value = inrAmount * (currency?.rate ?? 1);
      const isBase = (currency?.code || "INR") === "INR";
      // INR shows no decimals (whole rupees), foreign currencies keep 2dp
      // unless the caller explicitly hides them.
      const minFrac = isBase || opts?.hideDecimals ? 0 : 0;
      const maxFrac = isBase || opts?.hideDecimals ? 0 : 2;
      try {
        return new Intl.NumberFormat(currency?.locale || "en-US", {
          style: "currency",
          currency: currency?.code || "INR",
          minimumFractionDigits: minFrac,
          maximumFractionDigits: maxFrac,
          notation: opts?.compact ? "compact" : "standard",
        }).format(value);
      } catch {
        // Locale/currency combo not supported by the runtime — fall back
        // to a manual formatter so the UI never crashes on obscure regions.
        const rounded = value.toFixed(maxFrac);
        return `${currency?.symbol || currency?.code || ""}${Number(rounded).toLocaleString("en-US")}`;
      }
    },
    [currency],
  );

  const ctx = useMemo<CurrencyCtx>(() => ({
    base,
    code,
    currency,
    currencies,
    setCode,
    formatPrice,
    convertToDisplay,
    note: data?.note,
    isLoading,
  }), [base, code, currency, currencies, setCode, formatPrice, convertToDisplay, data?.note, isLoading]);

  return <CurrencyContext.Provider value={ctx}>{children}</CurrencyContext.Provider>;
};

export function useCurrency(): CurrencyCtx {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Graceful fallback so isolated tests / non-shopper pages still render.
    return {
      base: "INR",
      code: "INR",
      currency: DEFAULT_LADDER[0],
      currencies: DEFAULT_LADDER,
      setCode: () => {},
      formatPrice: (v) =>
        v == null ? "—" : `\u20B9${Math.round(v).toLocaleString("en-IN")}`,
      convertToDisplay: (v) => v,
      isLoading: false,
    };
  }
  return ctx;
}
